/**
 * Per-person availability math for the team page.
 *
 * Model (matches the capacity heatmap exactly, NOT the phase/demand engine):
 * a project consumes a person's capacity while TODAY is inside its
 * [start, end] window, using the raw hours_per_week on the assignment.
 * Status is irrelevant; there is no phase weighting.
 *
 * Pure + dependency-free so it can be unit-tested deterministically.
 */
export interface AssignedProject {
  id: string;
  name?: string;
  start: string | null;
  end: string | null;
  hours: number;
}

export interface PersonAvailability {
  assignedHrs: number; // hours from projects whose window covers today
  spareHrs: number; // capacity − assignedHrs
  capacity: number; // project-available hrs/wk (post support reserve)
  utilPct: number; // assignedHrs / capacity (0–1+)
  clears: string | null; // latest end among not-yet-ended commitments
  hasOpenEnded: boolean; // a not-ended commitment has no end date
}

/** Where a project sits relative to `todayMs` by its date window. */
export function projPhaseAt(
  p: { start: string | null; end: string | null },
  todayMs: number,
): { ended: boolean; upcoming: boolean; activeNow: boolean } {
  const s = p.start ? new Date(p.start).getTime() : null;
  const e = p.end ? new Date(p.end).getTime() : null;
  const ended = e !== null && e < todayMs;
  const upcoming = s !== null && s > todayMs;
  return { ended, upcoming, activeNow: !ended && !upcoming };
}

export function computeAvailability(
  projects: AssignedProject[],
  capacity: number,
  todayMs: number,
): PersonAvailability {
  const assignedHrs = projects
    .filter((p) => projPhaseAt(p, todayMs).activeNow)
    .reduce((s, p) => s + (p.hours ?? 0), 0);
  const spareHrs = capacity - assignedHrs;
  const utilPct = capacity > 0 ? assignedHrs / capacity : 0;
  const notEnded = projects.filter((p) => !projPhaseAt(p, todayMs).ended);
  const hasOpenEnded = notEnded.some((p) => !p.end);
  const ends = notEnded.filter((p) => p.end).map((p) => p.end as string).sort();
  const clears = ends.length ? ends[ends.length - 1] : null;
  return { assignedHrs, spareHrs, capacity, utilPct, clears, hasOpenEnded };
}
